// Writing styled .xlsx workbooks in the browser.
//
// Pen test AD25996 (final report AD26341, finding AD17). The styled exports
// (PMO dashboard, PLOW weekly report, Bid Tracker report) used xlsx-js-style,
// which carries its own frozen copy of SheetJS 0.18.5. It only ever wrote
// files, but the version string sat in the built bundle and a scanner reads it
// as CVE-2023-30533 every time. The usual replacement, exceljs, is no better:
// its browser build carries frozen copies of elliptic 6.5.4 and the browserify
// crypto shims, npm audit flags its uuid, and it has not had a release since
// 2023. write-excel-file zips anything over 160 KB in a blob: Web Worker, which
// our CSP (worker-src 'self') refuses, so a large export would hang silently.
//
// What these exports need from a spreadsheet is small: text, numbers and
// dates, fonts, fills, borders, alignment, number formats, column widths, row
// heights, merged cells, a filter and frozen panes. This module writes exactly
// that as Office Open XML and zips it with fflate's synchronous zipSync (no
// workers). It reads nothing, so there is no parser to attack.
//
// A workbook is a list of sheets:
//
//   {
//     name: 'PLOW',                          // 31 characters at most
//     rows: [[cell, ...], ...],              // row 0 is the first row
//     cols: [{ wch: 41 }, ...],              // width in characters
//     heights: [{ hpt: 55 }, ...],           // height in points, sparse
//     merges: [{ s: { r, c }, e: { r, c } }],
//     autofilter: 'A1:Z10',
//     freeze: { xSplit: 2, ySplit: 1 },      // columns and rows held still
//     validations: [{ ref: 'Q5:Q1004', list: ['Ready', 'Blocked'] }],  // drop-downs
//   }
//
// A cell is a plain value (string, number, boolean, Date, null) or
// { v, s, z, f }: the value, a style, a number format such as '£#,##0', and a
// formula (without the '='). With a formula, v is the value it shows until the
// spreadsheet recalculates, which it does on opening any workbook that has one.
// Styles use the shape xlsx-js-style used, so the builders kept their look:
//
//   { font: { name, sz, bold, italic, color: { rgb } },
//     fill: { patternType, fgColor: { rgb } },
//     border: { top: { style, color: { rgb } }, bottom, left, right },
//     alignment: { horizontal, vertical, wrapText } }
//
// Colours are 'RRGGBB' or 'AARRGGBB'. A Date is written as the wall-clock
// time the browser shows for it, as SheetJS did.
import { zipSync, strToU8 } from 'fflate';

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** 0 → 'A', 25 → 'Z', 26 → 'AA'. */
export function colName(c) {
  let s = '';
  for (let n = c + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

/** { r: 0, c: 0 } → 'A1'. */
export const cellRef = (r, c) => `${colName(c)}${r + 1}`;

// Characters XML 1.0 cannot carry are written the way Excel escapes them
// (_xHHHH_), and a literal _xHHHH_ in the text is escaped so Excel does not
// decode it.
const escapeXml = (s) => String(s)
  .replace(/_x([0-9A-Fa-f]{4})_/g, '_x005F_x$1_')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  // eslint-disable-next-line no-control-regex
  .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F￾￿]/g, ch => `_x${ch.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')}_`);

const argb = (rgb) => {
  const hex = String(rgb || '').replace(/^#/, '').toUpperCase();
  return hex.length === 6 ? `FF${hex}` : hex;
};

// Excel's serial day number for a Date, from its wall-clock fields.
const EPOCH = Date.UTC(1899, 11, 30);
function dateSerial(d) {
  const wall = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds());
  return (wall - EPOCH) / 864e5;
}

// Column width as Excel stores it, from a width in characters (SheetJS's
// conversion, so the columns come out exactly as wide as before).
const MDW = 6;
const colWidth = (wch) => Math.round(((wch * MDW + 5) / MDW) * 256) / 256;

// Number formats Excel has built in; anything else is declared from 164 up.
const BUILTIN_FORMATS = {
  General: 0, '0': 1, '0.00': 2, '#,##0': 3, '#,##0.00': 4, '0%': 9, '0.00%': 10,
  'm/d/yy': 14, 'd-mmm-yy': 15, 'd-mmm': 16, 'mmm-yy': 17, 'h:mm': 20, '@': 49,
};
const DEFAULT_DATE_FORMAT = 'm/d/yy';

const fontXml = (f = {}) =>
  `<font>${f.bold ? '<b/>' : ''}${f.italic ? '<i/>' : ''}${f.underline ? '<u/>' : ''}<sz val="${f.sz || 11}"/>`
  + `${f.color?.rgb ? `<color rgb="${argb(f.color.rgb)}"/>` : ''}<name val="${escapeXml(f.name || 'Calibri')}"/><family val="2"/></font>`;

const fillXml = (fl) => {
  if (!fl || !fl.fgColor?.rgb) return '<fill><patternFill patternType="none"/></fill>';
  return `<fill><patternFill patternType="${fl.patternType || 'solid'}"><fgColor rgb="${argb(fl.fgColor.rgb)}"/><bgColor indexed="64"/></patternFill></fill>`;
};

const borderXml = (b = {}) => ['left', 'right', 'top', 'bottom'].map((side) => {
  const e = b[side];
  if (!e || !e.style) return `<${side}/>`;
  return `<${side} style="${e.style}">${e.color?.rgb ? `<color rgb="${argb(e.color.rgb)}"/>` : '<color auto="1"/>'}</${side}>`;
}).join('') + '<diagonal/>';

// Collects the distinct fonts, fills, borders, formats and cell formats the
// workbook uses and hands back each cell's style index.
function styleTable() {
  const fonts = [fontXml()];
  const fills = [fillXml(null), '<fill><patternFill patternType="gray125"/></fill>'];
  const borders = [`<border>${borderXml()}</border>`];
  const formats = new Map();
  const xfs = ['<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'];
  const index = (list, xml) => {
    let i = list.indexOf(xml);
    if (i === -1) { i = list.length; list.push(xml); }
    return i;
  };
  const formatId = (code) => {
    if (code in BUILTIN_FORMATS) return BUILTIN_FORMATS[code];
    if (!formats.has(code)) formats.set(code, 164 + formats.size);
    return formats.get(code);
  };

  return {
    xf(s, z) {
      if (!s && !z) return 0;
      s = s || {};
      const numFmtId = z ? formatId(z) : 0;
      const fontId = s.font ? index(fonts, fontXml(s.font)) : 0;
      const fillId = s.fill ? index(fills, fillXml(s.fill)) : 0;
      const borderId = s.border ? index(borders, `<border>${borderXml(s.border)}</border>`) : 0;
      const a = s.alignment || {};
      const align = [
        a.horizontal ? ` horizontal="${a.horizontal}"` : '',
        a.vertical ? ` vertical="${a.vertical}"` : '',
        a.wrapText ? ' wrapText="1"' : '',
      ].join('');
      const xml = `<xf numFmtId="${numFmtId}" fontId="${fontId}" fillId="${fillId}" borderId="${borderId}" xfId="0"`
        + `${numFmtId ? ' applyNumberFormat="1"' : ''}${fontId ? ' applyFont="1"' : ''}${fillId ? ' applyFill="1"' : ''}`
        + `${borderId ? ' applyBorder="1"' : ''}${align ? ' applyAlignment="1"' : ''}>${align ? `<alignment${align}/>` : ''}</xf>`;
      return index(xfs, xml);
    },
    xml() {
      const numFmts = [...formats].map(([code, id]) => `<numFmt numFmtId="${id}" formatCode="${escapeXml(code)}"/>`).join('');
      return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
        + '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
        + (formats.size ? `<numFmts count="${formats.size}">${numFmts}</numFmts>` : '')
        + `<fonts count="${fonts.length}">${fonts.join('')}</fonts>`
        + `<fills count="${fills.length}">${fills.join('')}</fills>`
        + `<borders count="${borders.length}">${borders.join('')}</borders>`
        + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
        + `<cellXfs count="${xfs.length}">${xfs.join('')}</cellXfs>`
        + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
        + '</styleSheet>';
    },
  };
}

function sharedStrings() {
  const list = [];
  const seen = new Map();
  return {
    index(s) {
      if (!seen.has(s)) { seen.set(s, list.length); list.push(s); }
      return seen.get(s);
    },
    xml(count) {
      const items = list.map(s => `<si><t xml:space="preserve">${escapeXml(s)}</t></si>`).join('');
      return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
        + `<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${count}" uniqueCount="${list.length}">${items}</sst>`;
    },
  };
}

function paneXml(freeze) {
  const x = freeze?.xSplit || 0;
  const y = freeze?.ySplit || 0;
  if (!x && !y) return '';
  const topLeft = cellRef(y, x);
  const pane = x && y ? 'bottomRight' : y ? 'bottomLeft' : 'topRight';
  return `<pane${x ? ` xSplit="${x}"` : ''}${y ? ` ySplit="${y}"` : ''} topLeftCell="${topLeft}" activePane="${pane}" state="frozen"/>`
    + `<selection pane="${pane}" activeCell="${topLeft}" sqref="${topLeft}"/>`;
}

function sheetXml(sheet, first, styles, strings, counter) {
  const rows = sheet.rows || [];
  let maxCol = 0;
  const body = [];
  const rowCount = Math.max(rows.length, (sheet.heights || []).length);
  for (let r = 0; r < rowCount; r++) {
    const cells = [];
    for (const [c, raw] of (rows[r] || []).entries()) {
      const cell = raw !== null && typeof raw === 'object' && !(raw instanceof Date) ? raw : { v: raw };
      let v = cell.v;
      const isDate = v instanceof Date && !isNaN(v);
      const s = styles.xf(cell.s, cell.z || (isDate ? DEFAULT_DATE_FORMAT : undefined));
      const ref = cellRef(r, c);
      if (typeof v === 'number' && !isFinite(v)) v = null;
      if (cell.f) {
        counter.formulas++;
        const f = `<f>${escapeXml(String(cell.f).replace(/^=/, ''))}</f>`;
        if (typeof v === 'number') cells.push(`<c r="${ref}"${s ? ` s="${s}"` : ''}>${f}<v>${v}</v></c>`);
        else if (v != null && v !== '') cells.push(`<c r="${ref}"${s ? ` s="${s}"` : ''} t="str">${f}<v>${escapeXml(String(v))}</v></c>`);
        else cells.push(`<c r="${ref}"${s ? ` s="${s}"` : ''}>${f}</c>`);
      } else if (isDate) cells.push(`<c r="${ref}"${s ? ` s="${s}"` : ''}><v>${dateSerial(v)}</v></c>`);
      else if (typeof v === 'number') cells.push(`<c r="${ref}"${s ? ` s="${s}"` : ''}><v>${v}</v></c>`);
      else if (typeof v === 'boolean') cells.push(`<c r="${ref}"${s ? ` s="${s}"` : ''} t="b"><v>${v ? 1 : 0}</v></c>`);
      else if (v != null && v !== '' && !(v instanceof Date)) {
        counter.strings++;
        cells.push(`<c r="${ref}"${s ? ` s="${s}"` : ''} t="s"><v>${strings.index(String(v))}</v></c>`);
      } else if (s) cells.push(`<c r="${ref}" s="${s}"/>`);
      else continue;
      maxCol = Math.max(maxCol, c);
    }
    const hpt = sheet.heights?.[r]?.hpt;
    if (!cells.length && !hpt) continue;
    body.push(`<row r="${r + 1}"${hpt ? ` ht="${hpt}" customHeight="1"` : ''}>${cells.join('')}</row>`);
  }
  const cols = (sheet.cols || []).map((w, c) => (w && w.wch ? `<col min="${c + 1}" max="${c + 1}" width="${colWidth(w.wch)}" customWidth="1"/>` : '')).join('');
  const lastRow = Math.max(rows.length, 1);
  const dimension = `A1:${cellRef(lastRow - 1, maxCol)}`;
  const merges = (sheet.merges || []).map(m => `<mergeCell ref="${cellRef(m.s.r, m.s.c)}:${cellRef(m.e.r, m.e.c)}"/>`);
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
    + `<dimension ref="${dimension}"/>`
    + `<sheetViews><sheetView${first ? ' tabSelected="1"' : ''} workbookViewId="0">${paneXml(sheet.freeze)}</sheetView></sheetViews>`
    + '<sheetFormatPr defaultRowHeight="15"/>'
    + (cols ? `<cols>${cols}</cols>` : '')
    + `<sheetData>${body.join('')}</sheetData>`
    + (sheet.autofilter ? `<autoFilter ref="${sheet.autofilter}"/>` : '')
    + (merges.length ? `<mergeCells count="${merges.length}">${merges.join('')}</mergeCells>` : '')
    + validationsXml(sheet.validations)
    + '<pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>'
    + '</worksheet>';
}

// Drop-down lists. Excel caps a list typed into the rule at 255 characters.
function validationsXml(list) {
  if (!list?.length) return '';
  const rules = list.map(d => `<dataValidation type="list" allowBlank="1" showErrorMessage="1" sqref="${escapeXml(d.ref)}">`
    + `<formula1>${escapeXml(`"${d.list.join(',')}"`)}</formula1></dataValidation>`);
  return `<dataValidations count="${rules.length}">${rules.join('')}</dataValidations>`;
}

// A sheet name as Excel allows it: no []:*?/\ and 31 characters at most.
const safeSheetName = (name, i) => String(name || `Sheet${i + 1}`).replace(/[[\]:*?/\\]/g, ' ').slice(0, 31);

// $A$1:$Z$9 for a defined name.
const absoluteRange = (ref) => ref.split(':').map(p => p.replace(/^([A-Z]+)(\d+)$/, '$$$1$$$2')).join(':');

/** The workbook as .xlsx bytes. */
export function buildXlsx(sheets) {
  if (!sheets?.length) throw new Error('A workbook needs at least one sheet');
  const styles = styleTable();
  const strings = sharedStrings();
  const counter = { strings: 0, formulas: 0 };
  const names = sheets.map((s, i) => safeSheetName(s.name, i));
  const files = {};
  sheets.forEach((sheet, i) => {
    files[`xl/worksheets/sheet${i + 1}.xml`] = strToU8(sheetXml(sheet, i === 0, styles, strings, counter));
  });
  const definedNames = sheets.map((s, i) => (s.autofilter
    ? `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">'${escapeXml(names[i].replace(/'/g, "''"))}'!${absoluteRange(s.autofilter)}</definedName>`
    : '')).join('');

  files['[Content_Types].xml'] = strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
    + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
    + '<Default Extension="xml" ContentType="application/xml"/>'
    + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
    + sheets.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')
    + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
    + '<Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>'
    + '</Types>');
  files['_rels/.rels'] = strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
    + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
    + '</Relationships>');
  files['xl/workbook.xml'] = strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
    + '<bookViews><workbookView/></bookViews>'
    + `<sheets>${names.map((n, i) => `<sheet name="${escapeXml(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>`
    + (definedNames ? `<definedNames>${definedNames}</definedNames>` : '')
    // Formulas are written with the value Ops worked out; recalculate on
    // opening so they follow whatever is typed in from then on.
    + (counter.formulas ? '<calcPr calcId="191029" fullCalcOnLoad="1"/>' : '')
    + '</workbook>');
  files['xl/_rels/workbook.xml.rels'] = strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
    + sheets.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')
    + `<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`
    + `<Relationship Id="rId${sheets.length + 2}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>`
    + '</Relationships>');
  files['xl/styles.xml'] = strToU8(styles.xml());
  files['xl/sharedStrings.xml'] = strToU8(strings.xml(counter.strings));
  return zipSync(files, { level: 6 });
}

/** Build the workbook and hand it to the browser as a download. */
export function downloadXlsx(sheets, fileName) {
  const blob = new Blob([buildXlsx(sheets)], { type: XLSX_MIME });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Some browsers start the download after click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
